import { apiClient } from "../lib/api/client";

export interface CreditSummary {
    available_credits: number;
    total_earned: number;
    total_spent: number;
}

export interface CreditLedgerItem {
    id: string;
    type: string;
    amount: number;
    reason: string;
    reference_id?: string;
    created_at: string;
}

export interface CreditLedger {
    items: CreditLedgerItem[];
    page: number;
    limit: number;
    total: number;
    has_next: boolean;
}

export interface PricingFeature {
    key: string;
    label: string;
    description: string;
    cost: number;           // credits
    icon: string;
    cost_standard_minor: number;  // USD cents at Standard rate
    cost_premium_minor: number;   // USD cents at Premium rate
}

export interface PricingPlan {
    key: string;
    label: string;
    description: string;
    price_minor: number;    // USD cents per month (0 = free)
    credits: number;        // credits per billing period
    period: string;         // "month" | "once"
    rate_minor: number;     // USD cents per 1000 credits
    highlight: boolean;
    features: string[];
}

export interface CreditPricing {
    features: PricingFeature[];
    plans: PricingPlan[];
    grants: {
        onboarding: number;
        referral: number;
    };
}

// ── Module-level caches ───────────────────────────────────────────────────

const SUMMARY_TTL  = 60_000;       // 60 s
const PRICING_TTL  = 5 * 60_000;   // 5 min

let _summaryCache: { data: CreditSummary; at: number } | null = null;
let _pricingCache: { data: CreditPricing; at: number } | null = null;

type Listener = (summary: CreditSummary) => void;
const _listeners = new Set<Listener>();

export const creditService = {
    async getSummary(forceRefresh = false): Promise<CreditSummary> {
        if (!forceRefresh && _summaryCache && Date.now() - _summaryCache.at < SUMMARY_TTL) {
            return _summaryCache.data;
        }
        const data = await apiClient.get<CreditSummary>('/api/credits/summary');
        _summaryCache = { data, at: Date.now() };
        _listeners.forEach(fn => fn(data));
        return data;
    },

    invalidate() {
        _summaryCache = null;
        // Immediately refetch and broadcast to all listeners
        this.getSummary(true).catch(() => {});
    },

    onUpdate(fn: Listener): () => void {
        _listeners.add(fn);
        return () => _listeners.delete(fn);
    },

    async getPricing(forceRefresh = false): Promise<CreditPricing> {
        if (!forceRefresh && _pricingCache && Date.now() - _pricingCache.at < PRICING_TTL) {
            return _pricingCache.data;
        }
        const data = await apiClient.get<CreditPricing>('/api/credits/pricing');
        _pricingCache = { data, at: Date.now() };
        return data;
    },

    async getLedger(page = 1, limit = 20): Promise<CreditLedger> {
        return apiClient.get<CreditLedger>(`/api/credits/ledger?page=${page}&limit=${limit}`);
    },
};
